import _ from 'lodash';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Download } from '../../../assets/icons';
import { IconSize } from '../../../constant/IconSize.constant';
import { NUMBER } from '../../../constant/Number.constant';
import { useCharger } from '../../../hooks/useChargers';
import { useLicense } from '../../../hooks/useLicense';
import { Charger } from '../../../stores/types';
import { Button, ButtonSize, ButtonType } from '../../_ui';
import { ColumnConfig, ColumnsDropdown } from '../../_ui/data-table/columnsDropdown.component';
import InputSearch from '../../_ui/InputSearch/InputSearch.component';
import {
  FILTER_TYPES,
  LICENSE_PORT_TYPES,
  LICENSE_TYPES,
  LICENSE_TYPES_WITHOUT_PORT_VARIANTS,
} from '../utils';
import ChargerDropdown from './ChargerDropdown.component';
import ClearAllButton from './ClearAllButton.component';
import FilterDropDown from './FilterDropDown.component';
import RenderFilterPills from './RenderFilterPills.component';
import TypeDropdown from './TypeDropdown.component';

const LicenseFilters = () => {
  const { t } = useTranslation();
  const { chargers } = useCharger();
  const {
    totalSelectedFilters,
    licenseFilterquery,
    handleSelectFilter,
    columnVisibility,
    handleColumnVisibilityChange,
    handleResetColumns,
    handleExportAsCSV,
  } = useLicense();

  const columnConfigs: ColumnConfig[] = useMemo(
    () => [
      {
        id: 'licenseKey',
        label: t('license_key'),
        visible: columnVisibility.licenseKey,
        canHide: true,
      },
      {
        id: 'status',
        label: t('status'),
        visible: columnVisibility.status,
        canHide: true,
      },
      {
        id: 'assignedCharger',
        label: t('charger'),
        visible: columnVisibility.assignedCharger,
        canHide: true,
      },
      {
        id: 'type',
        label: t('license_type'),
        visible: columnVisibility.type,
        canHide: true,
      },
      {
        id: 'ports',
        label: t('ports'),
        visible: columnVisibility.ports,
        canHide: true,
      },
      {
        id: 'includes4G',
        label: t('license_inc_4g'),
        visible: columnVisibility.includes4G,
        canHide: true,
      },
      {
        id: 'duration',
        label: t('duration'),
        visible: columnVisibility.duration,
        canHide: true,
      },
      {
        id: 'remaining',
        label: t('license_remaining'),
        visible: columnVisibility.remaining,
        canHide: true,
      },
      {
        id: 'expirationDate',
        label: t('expires'),
        visible: columnVisibility.expirationDate,
        canHide: true,
      },
      {
        id: 'orderId',
        label: t('order_id'),
        visible: columnVisibility.orderId,
        canHide: true,
      },
    ],
    [columnVisibility, t],
  );

  const handleSearch = (item: any) => {
    const filteredSearches =
      licenseFilterquery.searchValue?.filter((search: string) => search === item) || [];

    let arr: any[] = licenseFilterquery.searchValue || [];
    if (filteredSearches.length > 0) {
      arr = [...(licenseFilterquery.searchValue || [])];
    } else {
      arr = [...(licenseFilterquery.searchValue || []), item];
    }
    handleSelectFilter(FILTER_TYPES.SEARCH_VALUE, arr);
  };

  const chargersGroupByLocation = _.groupBy(chargers, 'location.name');
  const ChargersWithLocation = Object.entries(chargersGroupByLocation)
    .map((item) => ({
      ...item,
      chargers: item[1],
    }))
    .filter((item) => item.chargers.length > NUMBER.ZERO);

  const chargersFilterDropdownList = useMemo(() => {
    return ChargersWithLocation.map((key: any) => {
      const children = key.chargers.map((charger: Charger) => {
        return {
          key: charger.id,
          id: charger.id,
          label: charger.name,
          selected: licenseFilterquery.chargerId?.includes(charger.id as string),
        };
      });

      return {
        id: key[0],
        label: key[0],
        selected: children?.every((child: any) => child.selected) || false,
        children: children || [],
      };
    });
  }, [licenseFilterquery, ChargersWithLocation]);

  const typeDropDownList = useMemo(() => {
    return Object.values(LICENSE_TYPES).map((type) => {
      const isSingleType = LICENSE_TYPES_WITHOUT_PORT_VARIANTS.includes(type);
      const childTypes = isSingleType ? [type] : Object.values(LICENSE_PORT_TYPES);
      const children = childTypes.map((port) => ({
        id: `${type}_${port}`,
        label: `${port}`,
        selected: licenseFilterquery.type?.includes(`${type}_${port}`),
      }));
      return {
        id: type,
        label: `${type}`,
        selected: children?.every((child: any) => child.selected) || false,
        children: children,
      };
    });
  }, [licenseFilterquery]);

  return (
    <>
      <div className='flex flex-row justify-between'>
        <div className='flex gap-3'>
          <ColumnsDropdown
            columns={columnConfigs}
            onColumnVisibilityChange={handleColumnVisibilityChange}
            onReset={handleResetColumns}
          />
          <InputSearch
            width='280px'
            iconSize={IconSize.SIZE_24x24}
            inputIconPadding='pl-11'
            suffixIconClassName='mr-4'
            options={[]}
            onSelect={handleSearch}
            placeholder={t('filter_by_license_or_order')}
            contentDivWidth='w-[277px]'
            disabled={false}
            inputId='license-input-search'
          />
          <FilterDropDown filterType={FILTER_TYPES.STATUS} />
          <ChargerDropdown chargersFilterDropdownList={chargersFilterDropdownList} />
          <TypeDropdown typeDropDownList={typeDropDownList} />
          <FilterDropDown filterType={FILTER_TYPES.INCLUDES_4G} />
          <FilterDropDown filterType={FILTER_TYPES.DURATION} />
        </div>
        <div className='flex gap-3'>
          <Button
            onClick={handleExportAsCSV}
            label={t('export_csv')}
            size={ButtonSize.SMALL}
            type={ButtonType.OUTLINE}
            icon={Download}
          />
        </div>
      </div>
      <div className='flex flex-row gap-2 items-baseline justify-between mt-2'>
        <RenderFilterPills
          chargersFilterDropdownList={chargersFilterDropdownList}
          typeDropDownList={typeDropDownList}
        />
        <div className='flex flex-row items-baseline min-w-fit'>
          {!!totalSelectedFilters && <ClearAllButton />}
        </div>
      </div>
    </>
  );
};

export default LicenseFilters;
